import type { AuthorizationDTO } from 'src/types/dto/authorization.dto';
import type { ActivityCompetencyDTO } from 'src/types/dto/competency.dto';
import type { IProjectRole } from 'src/types/project-role';
import { AuthorizationLevel } from 'src/types/authorization';
import {
  CompetencyHierarchy,
} from 'src/utils/competency-hierarchy';
import { authorizationSatisfiesCompetencyRequirement } from 'src/utils/activity-competency-coverage';

const levelHierarchy: Record<AuthorizationLevel, number> = {
  [AuthorizationLevel.EXPERT]: 1,
  [AuthorizationLevel.COMPETENT]: 2,
  [AuthorizationLevel.LEAD]: 3,
};

export function satisfiesLevelRequirement(
  inspectorLevel: AuthorizationLevel,
  requiredLevel: AuthorizationLevel | null,
  projectRoles: IProjectRole[] | null = null,
): boolean {
  if (requiredLevel === null) {
    return true;
  }

  if (levelHierarchy[inspectorLevel] === levelHierarchy[requiredLevel]) {
    return true;
  }

  if (projectRoles) {
    const relevantMobilities = projectRoles.filter((role) =>
      role.mobilities?.some((mobility) => mobility.level === requiredLevel) ?? false,
    );
    return relevantMobilities.some(
      (role) =>
        levelHierarchy[inspectorLevel] === levelHierarchy[role.level as AuthorizationLevel],
    );
  }

  return false;
}

function getEligibleProjectRoleIds(
  inspectorAuthLevel: AuthorizationLevel | null,
  projectRoles: IProjectRole[],
): number[] {
  if (!inspectorAuthLevel || !projectRoles.length) {
    return [];
  }

  const eligibleRoleIdsSet = new Set<number>();

  for (const role of projectRoles) {
    if (role.level === inspectorAuthLevel) {
      eligibleRoleIdsSet.add(role.id);
      (role.mobilities ?? []).forEach((mobility) => {
        eligibleRoleIdsSet.add(mobility.id);
      });
    }
  }

  return Array.from(eligibleRoleIdsSet);
}

export function isAuthorizationValidForRange(
  authorization: AuthorizationDTO,
  rangeStart?: Date,
  rangeEnd?: Date,
): boolean {
  const validFromTime =
    authorization.validFrom != null ? new Date(authorization.validFrom).getTime() : undefined;
  const validToTime =
    authorization.validTo != null ? new Date(authorization.validTo).getTime() : undefined;

  if (!rangeStart && !rangeEnd) {
    const now = Date.now();
    if (validFromTime !== undefined && validFromTime > now) {
      return false;
    }
    if (validToTime !== undefined && now > validToTime) {
      return false;
    }
    return true;
  }

  const rangeStartTime = rangeStart ? rangeStart.getTime() : undefined;
  const rangeEndTime = rangeEnd ? rangeEnd.getTime() : undefined;

  if (validToTime === undefined) {
    if (rangeEndTime !== undefined && validFromTime !== undefined) {
      return validFromTime <= rangeEndTime;
    }
    if (rangeStartTime !== undefined && validFromTime !== undefined) {
      return validFromTime <= rangeStartTime;
    }
    return validFromTime === undefined || validFromTime <= Date.now();
  }

  if (validFromTime === undefined) {
    if (rangeStartTime !== undefined) {
      return validToTime >= rangeStartTime;
    }
    if (rangeEndTime !== undefined) {
      return validToTime >= rangeEndTime;
    }
    return validToTime >= Date.now();
  }

  if (rangeStartTime !== undefined && rangeEndTime !== undefined) {
    return validFromTime <= rangeEndTime && validToTime >= rangeStartTime;
  }

  if (rangeStartTime !== undefined) {
    return validToTime >= rangeStartTime;
  }

  if (rangeEndTime !== undefined) {
    return validFromTime <= rangeEndTime;
  }

  const now = Date.now();
  return validFromTime <= now && validToTime >= now;
}

function getValidAuthorizations(
  authorizations: AuthorizationDTO[] | undefined,
  rangeStart?: Date,
  rangeEnd?: Date,
  accBodyIds?: number[],
): AuthorizationDTO[] {
  let validAuthorizations = (authorizations ?? []).filter((auth) =>
    isAuthorizationValidForRange(auth, rangeStart, rangeEnd),
  );

  if (accBodyIds && accBodyIds.length > 0) {
    validAuthorizations = validAuthorizations.filter(
      (auth) =>
        !auth.accreditationBody || accBodyIds.includes(auth.accreditationBody.id),
    );
  }

  return validAuthorizations;
}

export function getInspectorEligibleProjectRoleIds({
  authorizations,
  activityCompetencies,
  projectRoles,
  rangeStart,
  rangeEnd,
  accreditationBodyIds,
  competencyHierarchy = CompetencyHierarchy.empty(),
}: {
  authorizations?: AuthorizationDTO[];
  activityCompetencies?: ActivityCompetencyDTO[];
  projectRoles: IProjectRole[];
  rangeStart?: Date;
  rangeEnd?: Date;
  accreditationBodyIds?: number[];
  competencyHierarchy?: CompetencyHierarchy;
}): number[] {
  if (!projectRoles.length) {
    return [];
  }

  const activityCompetencyRequirements =
    activityCompetencies
      ?.map((ac) => ({
        competencyId: ac.competency?.id,
        level: ac.level ?? null,
      }))
      .filter((requirement) => requirement.competencyId != null) ?? [];

  if (!activityCompetencyRequirements.length) {
    return projectRoles.map((role) => role.id);
  }

  const validAuthorizations = getValidAuthorizations(
    authorizations,
    rangeStart,
    rangeEnd,
    accreditationBodyIds,
  );

  const eligibleRoleIdsSet = new Set<number>();

  for (const requirement of activityCompetencyRequirements) {
    const authForCompetency = validAuthorizations.find(
      (auth) =>
        auth.competency?.id != null &&
        auth.level != null &&
        authorizationSatisfiesCompetencyRequirement(
          auth,
          requirement.competencyId,
          requirement.level,
          undefined,
          projectRoles,
          competencyHierarchy,
        ),
    );

    if (!authForCompetency?.level) {
      continue;
    }

    const roleIds = getEligibleProjectRoleIds(authForCompetency.level, projectRoles);
    roleIds.forEach((roleId) => eligibleRoleIdsSet.add(roleId));
  }

  return Array.from(eligibleRoleIdsSet);
}
